import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CustomValidators } from '@shared-ikp/utils/validators';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { MeterPointChangeSignalState } from '../../../../../meterpoint-change-request.types';
import { ContractPartnerEmpty } from '../../../../new-contract-partner.utils';

export interface VertragspartnerForm {
  name: ReturnType<FormBuilder['control']>;
  customerNumber: ReturnType<FormBuilder['control']>;
  uid: ReturnType<FormBuilder['control']>;
  plz: ReturnType<FormBuilder['control']>;
  city: ReturnType<FormBuilder['control']>;
  street: ReturnType<FormBuilder['control']>;
  houseNumberStaircase: ReturnType<FormBuilder['control']>;
  door: ReturnType<FormBuilder['control']>;
  phone: ReturnType<FormBuilder['control']>;
  email: ReturnType<FormBuilder['control']>;
}

@Component({
  selector: 'ikp-vertragspartner-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './vertragspartner-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VertragspartnerFormComponent {
  protected form: FormGroup;

  private formBuilder = inject(FormBuilder);
  private wizardStore: MeterPointChangeSignalState = inject(SignalState);

  constructor() {
    const existingData = this.wizardStore.state().newContractPartner?.emptyPartner;
    this.form = this.buildForm(existingData);
  }

  private buildForm(model?: ContractPartnerEmpty): FormGroup {
    return this.formBuilder.group<VertragspartnerForm>({
      name: this.formBuilder.control(model?.name || '', [CustomValidators.companyNameValidator]),
      customerNumber: this.formBuilder.control(model?.gpNr || ''),
      uid: this.formBuilder.control(model?.uid || '', [CustomValidators.optionalUidValidator]),
      plz: this.formBuilder.control(model?.address?.plz || '', [CustomValidators.plzValidator]),
      city: this.formBuilder.control(model?.address?.city || '', [CustomValidators.cityValidator]),
      street: this.formBuilder.control(model?.address?.street || '', [CustomValidators.streetValidator]),
      houseNumberStaircase: this.formBuilder.control(model?.address?.houseNumberStaircase || '', [
        CustomValidators.houseNumberStaircaseValidator,
      ]),
      door: this.formBuilder.control(model?.address?.door || '', [CustomValidators.doorNumberValidator]),
      phone: this.formBuilder.control(model?.phone || '', [CustomValidators.optionalPhoneNumberValidator]),
      email: this.formBuilder.control(model?.email || '', [CustomValidators.optionalEmailValidator]),
    });
  }

  protected isInputInvalid(key: keyof VertragspartnerForm): boolean {
    const control = this.form.get(key);
    return control.touched && control.invalid;
  }

  protected getFormError(key: keyof VertragspartnerForm): string {
    const control = this.form.get(key);
    if (control.errors) {
      return control.errors.translationKey;
    }
    return null;
  }

  public get validForSubmission(): boolean {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    const isFormValid = this.form.valid;

    if (isFormValid) {
      this.updateStore();
    }
    return isFormValid;
  }

  private updateStore(): void {
    const formValue = this.form.value;
    const alternativePartner: ContractPartnerEmpty = {
      name: formValue.name?.trim(),
      gpNr: formValue.customerNumber?.trim() || undefined,
      uid: formValue.uid?.trim() || undefined,
      address: {
        plz: formValue.plz?.trim(),
        city: formValue.city?.trim(),
        street: formValue.street?.trim(),
        houseNumberStaircase: formValue.houseNumberStaircase?.trim(),
        door: formValue.door?.trim() || undefined,
      },
      phone: formValue.phone?.trim() || undefined,
      email: formValue.email?.trim() || undefined,
    };

    this.wizardStore.patchState({
      newContractPartner: {
        ...this.wizardStore.state().newContractPartner,
        emptyPartner: alternativePartner,
      },
    });
  }
}
